import { forwardRef, useState } from "react";
import type { ComponentProps, ReactNode } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
} from "react-native-reanimated";
import type { SharedValue } from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { type SheetAdapterRef } from "react-native-bottom-sheet-stack";
import { EventTicketsScreen, MerchOptionLines } from "@libs/shared-ui/native";
import type { OptionLine, OptionSet } from "@libs/buy-on-behalf-client/options";
import { goodsWords, type Goods } from "@libs/buy-on-behalf-client/goods";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";
import {
  APP_MODAL_CORNER_RADIUS,
  APP_MODAL_HANDLE_ROOM,
} from "@utils/appModal/constants";
import { useKeyboardHandler } from "./sheetKeyboard";
import { SheetGrow, SheetInsetProvider } from "./native/sheetHold";

/**
 * The light sheet every purchase in the app runs in, stacked over the
 * access Details: an engine event's tickets, a vendor's, a music unlock.
 * What it draws is the purchase's flow (PurchaseSheet); the tickets'
 * first step is TicketSelection.
 */

// Mirrors the light variant of the shared ticket screen.
const colors = {
  text: "#1d1d1f",
  secondary: "#6e6e73",
  surface: "#ffffff",
  primary: "#0082ec",
} as const;

/** The sheet's white, drawn from `inset` below the native sheet's top: the
 *  checkout draws the top lower while it closes in (SheetHold). */
const LightSurface = ({ inset }: { inset: SharedValue<number> }) => {
  const drawn = useAnimatedStyle(() => ({ top: inset.get() }));
  return <Animated.View style={[styles.surface, drawn]} />;
};

/**
 * The light modal sheet, with the inset SheetHold draws its top from.
 *
 * `keyboard`: who keeps the checkout above the keyboard.
 * - `"sheet"` (the engine's): the sheet insets its content by the keyboard's
 *   height (`keyboardBehavior="inset"`). Its only field natively is the card
 *   island's, at the checkout's foot.
 * - `"content"` (a vendor's, with its details to type): the sheet keeps its
 *   height and leaves the keyboard to its content. This sheet is native and
 *   springs to every new content height, so growing it by the keyboard (the
 *   library's inset, or padding) fights the keyboard's own animation. Instead
 *   the form scrolls (KeyboardAwareScrollView, VendorCheckout) and the button
 *   rides the keyboard (KeyboardStickyView, TicketsSheetFrame `sticky`), both
 *   frame by frame on the UI thread. Kept to the room under the handle.
 */
export const TicketsSheet = forwardRef<
  SheetAdapterRef,
  {
    keyboard?: "sheet" | "content";
    children: (context: { closeModal: () => void }) => ReactNode;
  }
>(function TicketsSheet({ keyboard = "sheet", children }, ref) {
  // How far below the native sheet's top the sheet is drawn.
  const inset = useSharedValue(0);
  const { height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  // A vendor's sheet grows to its room as the keyboard starts to come up
  // (its target height, not once it has settled): the native sheet springs
  // up with the keyboard, once, and the form gets the room the keyboard
  // takes. It goes back down as the keyboard goes.
  const [grown, setGrown] = useState(false);
  useKeyboardHandler(
    {
      onStart: (event) => {
        "worklet";
        if (keyboard === "content") runOnJS(setGrown)(event.height > 0);
      },
    },
    [keyboard],
  );
  const room = height - insets.top - APP_MODAL_HANDLE_ROOM;
  return (
    <AppModalSheet
      ref={ref}
      keyboardBehavior={keyboard === "sheet" ? "inset" : "none"}
      showHandle
      handleOffset={inset}
      surface={<LightSurface inset={inset} />}
    >
      {(context) => (
        <SheetInsetProvider value={inset}>
          {keyboard === "sheet" ? (
            children(context)
          ) : (
            // The native sheet's cap (the window under the status bar),
            // less the handle's room: a taller checkout scrolls its form;
            // grown, the sheet is that tall.
            <SheetGrow.Provider value={grown}>
              <View
                collapsable={false}
                style={grown ? { height: room } : { maxHeight: room }}
              >
                {children(context)}
              </View>
            </SheetGrow.Provider>
          )}
        </SheetInsetProvider>
      )}
    </AppModalSheet>
  );
});

type TicketsProps = Omit<
  ComponentProps<typeof EventTicketsScreen>,
  "variant" | "listMaxHeight" | "hostButton" | "onContinue"
>;

/** The ticket screen's own Continue: unused, its button is the sheet's. */
const sheetContinues = () => undefined;

/** What is bought, as tickets (the first step of PurchaseSheet, the
 *  contact step under it): loading, a retry when they could not load, then
 *  the shared ticket screen, its button drawn by the sheet. */
export function TicketSelection({
  loading,
  failed,
  onRetry,
  tickets,
  locked,
  goods,
  optionLabel,
  options,
}: {
  /** What is chosen: an event's tickets (absent), or a merch product's
   *  variants, named as Link in Bio names them (`goodsWords`). */
  goods?: Goods;
  /** What a product's variants are chosen by (the journey's
   *  `optionLabel`, "Colour"). */
  optionLabel?: string;
  /** A product chosen by its options (`optionSetOf` of the journey): a
   *  line per combination, a dropdown per option beside its quantity, as
   *  Link in Bio's sheet draws them, instead of the ticket types. */
  options?: {
    set: OptionSet;
    lines: OptionLine[];
    mostPerOrder: number | null;
    onChange(lines: OptionLine[]): void;
  };
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  tickets: TicketsProps;
  /** The contact step's code is up: nothing chosen may change. */
  locked: boolean;
}) {
  const { height } = useWindowDimensions();
  const words = goodsWords(goods);
  return (
    <>
      <Text accessibilityRole="header" style={styles.title}>
        {words.choose(
          options
            ? options.set.choices.map((choice) => choice.options.join(", "))
            : tickets.tiers.map((tier) => tier.name),
          optionLabel,
        )}
      </Text>
      {loading ? (
        <View style={styles.status}>
          <ActivityIndicator color={colors.secondary} />
          <Text style={styles.statusText}>{words.loading}</Text>
        </View>
      ) : failed ? (
        <Pressable
          accessibilityRole="button"
          onPress={onRetry}
          style={styles.status}
        >
          <Text style={styles.statusText}>{words.failed}</Text>
          <Text style={styles.retry}>Retry</Text>
        </Pressable>
      ) : options ? (
        <MerchOptionLines
          busy={tickets.busy || locked}
          lines={options.lines}
          mostPerOrder={options.mostPerOrder}
          onChange={options.onChange}
          set={options.set}
          tiers={tickets.tiers}
        />
      ) : (
        <EventTicketsScreen
          {...tickets}
          goods={goods}
          variant="light"
          listMaxHeight={height * 0.55}
          busy={tickets.busy || locked}
          hostButton
          onContinue={sheetContinues}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  surface: {
    ...StyleSheet.absoluteFill,
    backgroundColor: colors.surface,
    borderTopLeftRadius: APP_MODAL_CORNER_RADIUS,
    borderTopRightRadius: APP_MODAL_CORNER_RADIUS,
  },
  title: { color: colors.text, fontSize: 17, fontWeight: "600" },
  status: { alignItems: "center", gap: 8, paddingVertical: 24 },
  statusText: { color: colors.secondary, fontSize: 14 },
  retry: { color: colors.primary, fontSize: 14, fontWeight: "600" },
});
